import React from 'react';
import {
  DataList,
  Modal,
  ModalControls
} from '@loanpal/lumos__core';
import { Grid, View, Heading, Flex } from '@aws-amplify/ui-react';

/** Pull a displayable id from one offer entry (API / form / MultiSelect shapes). */
function offerItemToDisplayId(o) {
  if (o == null || o === '') return '';
  if (typeof o === 'string' || typeof o === 'number') return String(o).trim();
  if (typeof o === 'object') {
    const id = o.id ?? o.offerId ?? o.value ?? o.uuid;
    if (id != null && typeof id !== 'object') return String(id).trim();
    if (typeof o.value === 'object' && o.value != null) {
      const inner = offerItemToDisplayId(o.value);
      if (inner) return inner;
    }
  }
  return '';
}

/** Normalize SharedApp `offers` (AWSJSON) or preview payload JSON string for display. */
function formatSharedAppOffers(offers) {
  if (offers == null || offers === '') return '';
  let parsed = offers;
  if (typeof offers === 'string') {
    try {
      parsed = JSON.parse(offers);
    } catch {
      return offers;
    }
  }
  if (Array.isArray(parsed)) {
    return parsed.map((o) => offerItemToDisplayId(o)).filter(Boolean).join(', ');
  }
  if (parsed && typeof parsed === 'object') {
    const one = offerItemToDisplayId(parsed);
    return one || '';
  }
  return String(parsed);
}

function offersComparableKey(offers) {
  return formatSharedAppOffers(offers)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
    .join(',');
}

function fmtUseConnectedOrgs(useConnected) {
  return useConnected ? 'Yes' : 'No';
}

const UpdatePreview = ({previewCurrent, previewUpdate, isOpen, updateApp, toggleOpen}) => {
  const suCurrent = previewCurrent?.submittingUser ?? {};
  const suUpdate = previewUpdate?.submittingUser ?? {};
  const offersCurrentText = formatSharedAppOffers(previewCurrent?.offers);
  const offersUpdateText = formatSharedAppOffers(previewUpdate?.offers);
  const offersChanged = offersComparableKey(previewCurrent?.offers) !== offersComparableKey(previewUpdate?.offers);

  const ucCurrent = !!previewCurrent?.useConnectedOrgs;
  const ucUpdate = !!previewUpdate?.useConnectedOrgs;
  const pocCurrent = previewCurrent?.parentOrganizationClientId ?? '';
  const pocUpdate = previewUpdate?.parentOrganizationClientId ?? '';
  const oidCurrent = previewCurrent?.organizationId ?? '';
  const oidUpdate = previewUpdate?.organizationId ?? '';
  const connectedOrgFlagChanged = ucCurrent !== ucUpdate;
  const parentOrgClientIdChanged = pocCurrent !== pocUpdate;
  const organizationIdChanged = oidCurrent !== oidUpdate;
  const showConnectedOrgIds = ucCurrent || ucUpdate;

  const currentDataItems = previewCurrent ? [{
    label: 'Account Name',
    text: previewCurrent.partnerName
  }, {
    label: 'Channel',
    text: previewCurrent.channel
  }, {
    label: 'Category',
    text: previewCurrent.category
  }, {
    label: 'Offers',
    text: offersCurrentText || '—'
  }, {
    label: `${previewCurrent.calculatorType === "PA" ? "Payment Amount" : "Loan Amount"}`,
    text: previewCurrent.calculatorType === "PA" ? previewCurrent.paymentAmount : previewCurrent.loanAmount
  }, {
    label: 'Submitting User First Name',
    text: suCurrent.firstName ?? ''
  }, {
    label: 'Submitting User Last Name',
    text: suCurrent.lastName ?? ''
  }, {
    label: 'Submitting User Email',
    text: suCurrent.email ?? ''
  }, {
    label: 'Submitting User Phone',
    text: suCurrent.phone ?? ''
  }, {
    label: 'Use Connected Orgs',
    text: fmtUseConnectedOrgs(ucCurrent)
  }, ...(showConnectedOrgIds ? [{
    label: 'Parent Organization Client Id',
    text: pocCurrent || '—'
  }, {
    label: 'Organization Id',
    text: oidCurrent || '—'
  }] : [])] : [];

  const updateDataItems = previewUpdate ? [{
    label: 'Account Name',
    text: previewUpdate.partnerName
  }, {
    label: 'Channel',
    text: previewUpdate.channel !== previewCurrent?.channel ? <span style={styles.update}>{previewUpdate.channel}</span> : previewUpdate.channel
  }, {
    label: 'Category',
    text: previewUpdate.category !== previewCurrent?.category ? <span style={styles.update}>{previewUpdate.category}</span> : previewUpdate.category
  }, {
    label: 'Offers',
    text: offersChanged ? <span style={styles.update}>{offersUpdateText || '—'}</span> : (offersUpdateText || '—')
  }, {
    label: `${previewUpdate.calculatorType === "PA" ? "Payment Amount" : "Loan Amount"}`,
    text: previewUpdate.calculatorType === "PA" ? previewUpdate.paymentAmount : previewUpdate.loanAmount
  }, {
    label: 'Submitting User First Name',
    text: suUpdate.firstName !== suCurrent.firstName ? <span style={styles.update}>{suUpdate.firstName ?? ''}</span> : (suUpdate.firstName ?? '')
  }, {
    label: 'Submitting User Last Name',
    text: suUpdate.lastName !== suCurrent.lastName ? <span style={styles.update}>{suUpdate.lastName ?? ''}</span> : (suUpdate.lastName ?? '')
  }, {
    label: 'Submitting User Email',
    text: suUpdate.email !== suCurrent.email ? <span style={styles.update}>{suUpdate.email ?? ''}</span> : (suUpdate.email ?? '')
  }, {
    label: 'Submitting User Phone',
    text: suUpdate.phone !== suCurrent.phone ? <span style={styles.update}>{suUpdate.phone ?? ''}</span> : (suUpdate.phone ?? '')
  }, {
    label: 'Use Connected Orgs',
    text: connectedOrgFlagChanged ? (
      <span style={styles.update}>{fmtUseConnectedOrgs(ucUpdate)}</span>
    ) : fmtUseConnectedOrgs(ucUpdate)
  }, ...(showConnectedOrgIds ? [{
    label: 'Parent Organization Client Id',
    text: parentOrgClientIdChanged ? (
      <span style={styles.update}>{pocUpdate || '—'}</span>
    ) : (pocUpdate || '—')
  }, {
    label: 'Organization Id',
    text: organizationIdChanged ? (
      <span style={styles.update}>{oidUpdate || '—'}</span>
    ) : (oidUpdate || '—')
  }] : [])] : [];

  return (
      <Modal variant="full" isOpen={isOpen} title="Preview QuickLink Update" onDismiss={() => toggleOpen(false)}>
        <Grid
          templateColumns="1fr 1fr"
          templateRows="50rem"
          gap="0.5rem"
        >
          {previewCurrent && (
          <View backgroundColor="#d7d7d7" padding="1rem">
            <Flex
              direction="column"
              alignItems="flex-start"
              gap="0.5rem"
            >
              <Heading level={6}>
                Current
              </Heading>
              <DataList columns={[1]} items={currentDataItems} />
            </Flex>
          </View>)}
          {previewUpdate && (
          <View backgroundColor="#d7d7d7" padding="1rem">
            <Flex
              direction="column"
              alignItems="flex-start"
              gap="0.5rem"
            >
              <Heading level={6}>
                Update
              </Heading>
              <DataList columns={[1]} items={updateDataItems} />
            </Flex>
          </View>)}
        </Grid>
        <ModalControls actions={[
            {
              onClick: () => updateApp(),
              label: 'Update',
            },
            {
              onClick: () => toggleOpen(false), label: 'Cancel'
            }
          ]}
        />
      </Modal>
  );
}

const styles = {
  update: {
    color: "#c00"
  }
}
export default UpdatePreview;